import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { CheckCircle, Info, NotificationsNone, Warning } from '@mui/icons-material';
import { Button, FocusedElement, FocusedElementTooltip, ModalActions, ModalTitle, StickyStackItem } from '@uplift/ui-v2';
import { useMemo, useState } from 'react';
import { ProfileOnboardingStatus, getInvalidRows } from '../profile.utils';
import { useProfileContext } from '../profile.context';
import { SubmitProfileModal } from './SubmitProfileModal.component';

export type ProfileAlertBarProps = {
  status: ProfileOnboardingStatus;

  /*
    * These two props are for the review status action button
    */
  onToggleIsEditProfile: () => void;
  isEditProfile: boolean;
};

export const ProfileAlertBar: React.VFC<ProfileAlertBarProps> = props => {
  const { profile } = useProfileContext();
  const [ focused, setFocused ] = useState(true);
  const [ confirmSubmissionModal, setConfirmSubmissionModal ] = useState(false);
  const { classes } = useAlertBarStyles();

  const onCloseConfirmSubmissionModal = () => setConfirmSubmissionModal(false);
  const onUnfocus = () => setFocused(false);

  const onReviewRequiredFields = () => {
    onUnfocus();

    const invalidRows = getInvalidRows(profile);

    if (invalidRows.length > 0) {
      const firstInvalidRow = invalidRows[0];

      const row = document.getElementById(firstInvalidRow);

      if (row) {
        row.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  };

  switch (props.status) {
    case ProfileOnboardingStatus.ready_to_submit:
      return (
        <>
          <SubmitProfileModal open={confirmSubmissionModal} onClose={onCloseConfirmSubmissionModal} />

          <AlertBar
            focused={focused}
            onUnfocus={onUnfocus}
            type="secondary"
            action={(
              <Button
                variant="contained"
                color="secondary"
                onClick={() => setConfirmSubmissionModal(true)}
                className={classes.noWrap}
                size="small"
              >
                Submit
              </Button>
            )}
            focusedTooltipContent={(
              <Box width="328px">
                <ModalTitle onClose={onUnfocus}>Ready to Submit</ModalTitle>
                <Box className={classes.popupContent}>
                  We're here to set you up for success: As a last step, you'll need to submit your profile to us for review.
                  <br /><br />
                  We will approve it if it's good to go or send you any feedback that we think could make it stronger.
                </Box>
              </Box>
            )}
          >
            Your profile is ready to submit for review
          </AlertBar>
        </>
      );
    case ProfileOnboardingStatus.incomplete:
      return (
        <AlertBar
          type="warning"
          focused={focused}
          onUnfocus={onUnfocus}
          action={(
            <Button
              variant="contained"
              color="secondary"
              disabled
              className={classes.noWrap}
              size="small"
            >
              Submit
            </Button>
          )}
          focusedTooltipContent={(
            <Box width="328px">
              <ModalTitle onClose={onUnfocus}>Almost done</ModalTitle>
              <Box className={classes.popupContent}>
                Your profile is almost done—there's just a few required fields left.
                <br /><br />
                Finish filling those out then come back here to submit your profile for review.
              </Box>
              <ModalActions>
                <Button variant="contained" color="secondary" fullWidth onClick={onReviewRequiredFields}>review required fields</Button>
              </ModalActions>
            </Box>
          )}
        >
          Your profile is incomplete. Fill out all required fields to submit it for review.
        </AlertBar>
      );
    case ProfileOnboardingStatus.in_review:
      return (
        <AlertBar
          type="info"
          action={(
            <Button variant="contained" color="secondary" size="small" onClick={props.onToggleIsEditProfile}>
              {props.isEditProfile ? 'Resubmit' : 'Edit Profile'}
            </Button>
          )}
        >
          Your profile is in review. We suggest waiting for feedback before making any edits.
        </AlertBar>
      );
    case ProfileOnboardingStatus.approved:
      return (
        <AlertBar type="success">
          Your profile has been approved. Once you go live on UpLift, you can change your profile whenever you want.
        </AlertBar>
      );
    default:
      return null;
  }
};

type AlertBarProps = {
  type: 'secondary' | 'warning' | 'info' | 'success';
  children: React.ReactNode;
  action?: React.ReactNode;
  focused?: boolean;
  onUnfocus?: () => void;
  focusedTooltipContent?: React.ReactNode;
};

const AlertBar: React.VFC<AlertBarProps> = props => {
  const { classes, cx } = useAlertBarStyles();

  const icon = useMemo(() => {
    switch (props.type) {
      case 'secondary':
        return (
          <Box className={classes.secondaryIconContainer}>
            <NotificationsNone fontSize="small" />
          </Box>
        );
      case 'warning':
        return <Warning className={classes.textWarning} />;
      case 'info':
        return <Info className={classes.textInfo} />;
      case 'success':
        return <CheckCircle className={classes.textSuccess} />;
    }
  }, [ classes.secondaryIconContainer, classes.textInfo, classes.textSuccess, classes.textWarning, props.type ]);

  return (
    <StickyStackItem order={0} placement="top">
      <div className={classes.alertBarContainer}>
        <FocusedElement open={props.focused} onClose={props.onUnfocus}>
          <Box className={cx(classes.alertBar, {
            [classes.bgSecondaryBackground]: props.type === 'secondary',
            [classes.bgWarningBackground]: props.type === 'warning',
            [classes.bgInfoBackground]: props.type === 'info',
            [classes.bgSuccessBackground]: props.type === 'success',
          })}
          >
            <Box display="flex" alignItems="center" gap="0.5rem">
              {icon}
              <Typography className={classes.fontWeight450}>
                {props.children}
              </Typography>
            </Box>
            <FocusedElementTooltip
              open={props.focused}
              topSpacing={24}
              content={props.focusedTooltipContent}
            >
              <Box>
                {props.action}
              </Box>
            </FocusedElementTooltip>
          </Box>
        </FocusedElement>
      </div>
    </StickyStackItem>
  );
};

const useAlertBarStyles = makeStyles({ name: 'AlertBar' })(theme => ({
  alertBarContainer: {
    zIndex: theme.zIndex.appBar + 2,
  },
  alertBar: {
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(3),
    padding: theme.spacing(2),
  },
  secondaryIconContainer: {
    backgroundColor: theme.palette.secondary.main,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    color: theme.palette.secondary.background,
  },
  fontWeight450: {
    fontWeight: 450,
  },
  noWrap: {
    whiteSpace: 'nowrap',
  },
  popupContent: {
    padding: theme.spacing(0, 3, 5),
  },

  textWarning: {
    color: theme.palette.warning.main,
  },
  textInfo: {
    color: theme.palette.info.main,
  },
  textSuccess: {
    color: theme.palette.success.main,
  },

  bgSecondaryBackground: {
    backgroundColor: theme.palette.secondary.background,
  },
  bgWarningBackground: {
    backgroundColor: theme.palette.warning.background,
  },
  bgInfoBackground: {
    backgroundColor: theme.palette.info.background,
  },
  bgSuccessBackground: {
    backgroundColor: theme.palette.success.background,
  },
}));
